import type {
  EnvironmentUiContextQuery,
  EnvironmentUiContextSubject,
} from "@excellent/ui/api/contracts/environments";

export const CANONICAL_ENVIRONMENT_QUERY_KEYS = [
  "environmentOrigin",
  "environmentId",
  "environmentGeneration",
  "environmentScope",
] as const;

export type CanonicalEnvironmentSelectionIntent =
  | { kind: "default" }
  | { kind: "environment"; subject: EnvironmentUiContextSubject }
  | { kind: "all" };

export type CanonicalEnvironmentRouteSelection =
  | { kind: "absent" }
  | { kind: "valid"; selection: CanonicalEnvironmentSelectionIntent }
  | {
      kind: "invalid";
      reason:
        | "incomplete-environment-subject"
        | "conflicting-environment-scope"
        | "invalid-environment-scope"
        | "duplicate-environment-parameter";
    };

function queryParams(search: string): URLSearchParams {
  return new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
}

function hasCanonicalEnvironmentKey(params: URLSearchParams): boolean {
  return CANONICAL_ENVIRONMENT_QUERY_KEYS.some((key) => params.has(key));
}

export function parseCanonicalEnvironmentRouteSelection(
  search: string,
): CanonicalEnvironmentRouteSelection {
  const params = queryParams(search);
  if (!hasCanonicalEnvironmentKey(params)) return { kind: "absent" };
  if (CANONICAL_ENVIRONMENT_QUERY_KEYS.some((key) => params.getAll(key).length > 1)) {
    return { kind: "invalid", reason: "duplicate-environment-parameter" };
  }
  const scope = params.get("environmentScope");
  const originTenantId = params.get("environmentOrigin")?.trim() ?? "";
  const environmentId = params.get("environmentId")?.trim() ?? "";
  const generationId = params.get("environmentGeneration")?.trim() ?? "";
  const tupleCount = [originTenantId, environmentId, generationId].filter(Boolean).length;
  if (scope !== null && scope !== "all" && scope !== "default")
    return { kind: "invalid", reason: "invalid-environment-scope" };
  if (scope === "default") {
    return tupleCount === 0
      ? { kind: "valid", selection: { kind: "default" } }
      : { kind: "invalid", reason: "conflicting-environment-scope" };
  }
  if (scope === "all") {
    return tupleCount === 0
      ? { kind: "valid", selection: { kind: "all" } }
      : { kind: "invalid", reason: "conflicting-environment-scope" };
  }
  if (tupleCount !== 3) return { kind: "invalid", reason: "incomplete-environment-subject" };
  return {
    kind: "valid",
    selection: { kind: "environment", subject: { originTenantId, environmentId, generationId } },
  };
}

export function environmentUiContextQueryForSelection(
  selection: CanonicalEnvironmentSelectionIntent,
): EnvironmentUiContextQuery {
  if (selection.kind === "default") return { scope: "default" };
  if (selection.kind === "all") return { scope: "all" };
  return {
    scope: "environment",
    originTenantId: selection.subject.originTenantId,
    environmentId: selection.subject.environmentId,
    generationId: selection.subject.generationId,
  };
}

export function canonicalEnvironmentIntentKey(
  selection: CanonicalEnvironmentSelectionIntent,
): string {
  if (selection.kind !== "environment") return selection.kind;
  const { originTenantId, environmentId, generationId } = selection.subject;
  return `environment:${JSON.stringify([originTenantId, environmentId, generationId])}`;
}

let canonicalEnvironmentRequestGeneration = 0;

/**
 * Advance the client-side generation whenever the canonical target changes.
 * This is a cache/late-response guard only; server authority remains the
 * source of truth for every Environment request.
 */
export function advanceCanonicalEnvironmentRequestGeneration(): number {
  canonicalEnvironmentRequestGeneration += 1;
  return canonicalEnvironmentRequestGeneration;
}

export function currentCanonicalEnvironmentRequestGeneration(): number {
  return canonicalEnvironmentRequestGeneration;
}

function removeCanonicalEnvironmentQuery(params: URLSearchParams): void {
  for (const key of CANONICAL_ENVIRONMENT_QUERY_KEYS) params.delete(key);
}

function applyCanonicalEnvironmentSelection(
  params: URLSearchParams,
  selection: CanonicalEnvironmentSelectionIntent,
): void {
  removeCanonicalEnvironmentQuery(params);
  if (selection.kind === "default") params.set("environmentScope", "default");
  if (selection.kind === "all") params.set("environmentScope", "all");
  if (selection.kind === "environment") {
    params.set("environmentOrigin", selection.subject.originTenantId);
    params.set("environmentId", selection.subject.environmentId);
    params.set("environmentGeneration", selection.subject.generationId);
  }
}

function splitInternalHref(to: string) {
  // Protocol-relative URLs are external even when a browser-equivalent
  // backslash form is used.
  if (!to.startsWith("/") || /^\/[\\/]/.test(to)) return null;
  const hashIndex = to.indexOf("#");
  const hash = hashIndex >= 0 ? to.slice(hashIndex) : "";
  const withoutHash = hashIndex >= 0 ? to.slice(0, hashIndex) : to;
  const queryIndex = withoutHash.indexOf("?");
  const pathname = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const destinationQueryText = queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : "";
  return { pathname, params: new URLSearchParams(destinationQueryText), hash };
}

function joinInternalHref(parts: NonNullable<ReturnType<typeof splitInternalHref>>): string {
  const query = parts.params.toString();
  return `${parts.pathname}${query ? `?${query}` : ""}${parts.hash}`;
}

export function hrefWithCanonicalEnvironmentSelection(
  to: string,
  selection: CanonicalEnvironmentSelectionIntent,
): string {
  const parts = splitInternalHref(to);
  if (!parts) return to;
  parts.params.delete("env");
  applyCanonicalEnvironmentSelection(parts.params, selection);
  return joinInternalHref(parts);
}

/** Remove an explicit Environment target while preserving unrelated URL state. */
export function hrefWithoutCanonicalEnvironmentSelection(to: string): string {
  const parts = splitInternalHref(to);
  if (!parts) return to;
  removeCanonicalEnvironmentQuery(parts.params);
  return joinInternalHref(parts);
}

/** Preserve canonical scope when present while keeping legacy links safe until
 * their individual product surface adopts the canonical subject contract. */
export function preserveEnvironmentHref(to: string, currentSearch: string): string {
  const parts = splitInternalHref(to);
  if (!parts) return to;
  const current = parseCanonicalEnvironmentRouteSelection(currentSearch);
  if (hasCanonicalEnvironmentKey(parts.params)) {
    parts.params.delete("env");
  } else if (current.kind === "valid" && current.selection.kind !== "default") {
    parts.params.delete("env");
    applyCanonicalEnvironmentSelection(parts.params, current.selection);
  }
  const next = joinInternalHref(parts);
  return next !== to ? next : to;
}
